import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api } from './api.js';

const ContexteAuth = createContext(null);

/**
 * État d'authentification partagé par l'application.
 *
 * La session vit dans un cookie httpOnly, illisible par le JavaScript de la
 * page : le front ne peut donc pas savoir s'il est connecté autrement qu'en
 * demandant au serveur. C'est le rôle de l'appel à /auth/me au démarrage.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    let annule = false;

    api
      .get('/auth/me')
      .then((data) => !annule && setUser(data.user))
      // 401 attendu quand personne n'est connecté : ce n'est pas une erreur.
      .catch(() => !annule && setUser(null))
      .finally(() => !annule && setChargement(false));

    return () => {
      annule = true;
    };
  }, []);

  const valeur = useMemo(
    () => ({
      user,
      chargement,
      inscription: async (donnees) => {
        const { user: cree } = await api.post('/auth/register', donnees);
        setUser(cree);
        return cree;
      },
      connexion: async (donnees) => {
        const { user: connecte } = await api.post('/auth/login', donnees);
        setUser(connecte);
        return connecte;
      },
      enregistrerPreferences: async (donnees) => {
        const { user: aJour } = await api.patch('/auth/preferences', donnees);
        setUser(aJour);
        return aJour;
      },
      deconnexion: async () => {
        await api.post('/auth/logout');
        setUser(null);
      },
    }),
    [user, chargement],
  );

  return <ContexteAuth.Provider value={valeur}>{children}</ContexteAuth.Provider>;
}

export function useAuth() {
  const contexte = useContext(ContexteAuth);
  if (!contexte) throw new Error('useAuth doit être utilisé dans un AuthProvider.');
  return contexte;
}
